import type { MedplumClient } from '@medplum/core';
import type { Practitioner, Task } from '@medplum/fhirtypes';

import {
  formatPractitionerPreferredLabel,
  getSupervisorRefs,
  getSupervisingPractitionerRef,
} from '@unify/fhir-model/resources/practitioner';
import type { VariableContext } from './registry';

const NPI_SYSTEM = 'http://hl7.org/fhir/sid/us-npi';

function getNpi(p: Practitioner | undefined): string | undefined {
  return p?.identifier?.find((id) => id.system === NPI_SYSTEM)?.value;
}

function getLicenseNumber(p: Practitioner | undefined): string | undefined {
  const qual = p?.qualification?.find(
    (q) =>
      q.code?.text?.toLowerCase().includes('license') ||
      q.code?.coding?.some((c) => c.code?.toLowerCase().includes('license')),
  );
  return qual?.identifier?.[0]?.value ?? qual?.code?.text;
}

export function practitionerToSupervisorContext(
  p: Practitioner | undefined,
): VariableContext['supervisor'] | undefined {
  if (!p) return undefined;
  const name = formatPractitionerPreferredLabel(p) || undefined;
  if (!name && !getNpi(p) && !getLicenseNumber(p)) return undefined;
  return {
    name,
    fullName: name,
    licenseNumber: getLicenseNumber(p),
    npiNumber: getNpi(p),
  };
}

export function getSupervisingPractitionerReference(
  practitioner: Practitioner | undefined,
): string | undefined {
  return getSupervisingPractitionerRef(practitioner ?? { resourceType: 'Practitioner' })?.reference;
}

export function practitionerHasSupervisor(practitioner: Practitioner | undefined): boolean {
  if (!practitioner) return false;
  return getSupervisorRefs(practitioner).length > 0;
}

export function practitionerIdFromReference(reference: string | undefined): string | undefined {
  if (!reference?.startsWith('Practitioner/')) return undefined;
  return reference.replace(/^Practitioner\//, '');
}

/** Practitioner id from Task.requester when the assigner is a provider. */
export function assignerPractitionerIdFromTask(task: Task | undefined): string | undefined {
  return practitionerIdFromReference(task?.requester?.reference);
}

export async function loadSupervisorContextForPractitioner(
  medplum: MedplumClient,
  practitioner: Practitioner | undefined,
): Promise<VariableContext['supervisor'] | undefined> {
  const supervisorRef = getSupervisingPractitionerReference(practitioner);
  if (!supervisorRef?.startsWith('Practitioner/')) return undefined;
  const supervisorId = supervisorRef.replace(/^Practitioner\//, '');
  try {
    const supervisor = await medplum.readResource('Practitioner', supervisorId);
    return practitionerToSupervisorContext(supervisor);
  } catch {
    return undefined;
  }
}
